import { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import type { HotelFacets } from '../../../api/types/hotel';

/* ------------------------------------------------------------------------ *
 * Otel filtre paneli — sol ray. Metin/iade filtreleri sonuç listesinden
 * türetilir; YILDIZ / FİYAT / TESİS facet'leri ise `Hotel/GetFilter`'ın
 * (searchHotels) döndürüp `HotelSearchResponse.filters` altında taşıdığı
 * SUNUCU-taraflı `HotelFacets` verisinden gelir — yoksa (bölgede sonuç yok →
 * hepsi `null`) sonuçtan türetilen fallback listeye düşer.
 *
 * Filtreleme İSTEMCİ tarafında yapılıyor (en basit yol): panel yalnızca
 * `filters` state'ini günceller, `ResultsStep` bu state'e göre `priceResults`'ı
 * eler. `HotelSearchRequest` `stars[]`/`minPrice`/`maxPrice`/`hotelPropertiesList`
 * alanlarını taşıdığı için ileride yeniden-sorgu (server-side) yoluna geçilebilir;
 * o zaman bu panelin `onChange`'i yeni bir `searchHotels` çağrısını tetikler.
 * ------------------------------------------------------------------------ */

export type RefundFilter = 'all' | 'withPolicy' | 'noPolicy';

export interface FiltersState {
  hotelName: string;
  region: string;
  subRegion: string;
  refund: RefundFilter;
  stars: Set<number>;
  boards: Set<string>;
  facilities: Set<string>;
  priceRange: [number, number];
}

/** `HotelFilter.filterName` sunucuda sayacı metne gömülü döner
 *  (`"Air conditioning (25) "`, sondaki boşluk dahil). Taban adı + sayacı ayır;
 *  eşleşme yoksa sadece kırpılmış adı döndür. */
function parseFacetLabel(raw?: string): { name: string; count?: number } | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const match = /^(.*?)\s*\((\d+)\)$/.exec(trimmed);
  if (match) return { name: match[1].trim(), count: Number(match[2]) };
  return { name: trimmed };
}

const toggleSetValue = <T,>(set: Set<T>, value: T): Set<T> => {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
};

interface StarOption {
  star: number;
  label: string;
  count: number;
}

export function HotelFilterPanel({
  filters,
  setFilters,
  priceBounds,
  starOptions,
  boardOptions,
  facilityOptions,
  serverFacets,
}: {
  filters: FiltersState;
  setFilters: React.Dispatch<React.SetStateAction<FiltersState>>;
  /** Efektif fiyat sınırları — `ResultsStep` sonuç listesi + sunucu `minPrice`/`maxPrice`
   *  birleşimini hesaplayıp geçirir. */
  priceBounds: [number, number];
  /** Sonuçtan türetilen fallback yıldız sayaçları (`[star, count]`). */
  starOptions: [number, number][];
  boardOptions: [string, number][];
  /** Sonuçtan türetilen fallback tesis sayaçları (`[name, count]`). */
  facilityOptions: [string, number][];
  serverFacets?: HotelFacets;
}) {
  const [facilitySearch, setFacilitySearch] = useState('');

  // Yıldız: sunucu `starGroups` varsa onu kullan (sayacı sonuçtan tamamla), yoksa fallback.
  const effectiveStarOptions = useMemo<StarOption[]>(() => {
    const groups = serverFacets?.starGroups;
    const clientCounts = new Map(starOptions);
    if (groups && groups.length) {
      return groups
        .filter((g): g is { star: number; caption?: string } => typeof g.star === 'number')
        .map((g) => ({
          star: g.star,
          // `caption` backend'den ham HTML (`<input>…<Label>…`) olarak geliyor —
          // bkz. RateHawkUtils.cs/PaximumUtils.cs `StarGroup.Caption`; render edilebilir
          // metin değil, kullanılmıyor.
          label: `${g.star} yıldız`,
          count: clientCounts.get(g.star) ?? 0,
        }))
        .sort((a, b) => b.star - a.star);
    }
    return starOptions.map(([star, count]) => ({ star, label: `${star} yıldız`, count }));
  }, [serverFacets, starOptions]);

  // Tesis: sunucu `hotelPropertiesList` + `hotelServicesList` (+ `roomPropertiesList`)
  // varsa onlardan, yoksa sonuçtan türetilen fallback listeden.
  const effectiveFacilityOptions = useMemo<[string, number][]>(() => {
    const server = [
      ...(serverFacets?.hotelPropertiesList ?? []),
      ...(serverFacets?.hotelServicesList ?? []),
      ...(serverFacets?.roomPropertiesList ?? []),
    ];
    if (server.length) {
      const clientCounts = new Map(facilityOptions);
      const map = new Map<string, number>();
      server.forEach((f) => {
        const parsed = parseFacetLabel(f?.filterName ?? f?.hotelName ?? undefined);
        if (!parsed) return;
        map.set(parsed.name, parsed.count ?? clientCounts.get(parsed.name) ?? 0);
      });
      return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0], 'tr'));
    }
    return facilityOptions;
  }, [serverFacets, facilityOptions]);

  const visibleFacilities = useMemo(() => {
    const q = facilitySearch.trim().toLocaleLowerCase('tr');
    const list = q
      ? effectiveFacilityOptions.filter(([name]) => name.toLocaleLowerCase('tr').includes(q))
      : effectiveFacilityOptions;
    // Aramasız durumda seçili olanlar + ilk 15 — 200 kutucuk basmamak için.
    if (q) return list;
    const selected = list.filter(([name]) => filters.facilities.has(name));
    const rest = list.filter(([name]) => !filters.facilities.has(name)).slice(0, 15);
    return [...selected, ...rest];
  }, [effectiveFacilityOptions, facilitySearch, filters.facilities]);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
        <FilterAltIcon fontSize="small" color="action" />
        <Typography variant="subtitle1">Filtreler</Typography>
      </Stack>

      <Stack spacing={2}>
        <TextField
          label="Otel Adı"
          size="small"
          fullWidth
          value={filters.hotelName}
          onChange={(e) => setFilters((f) => ({ ...f, hotelName: e.target.value }))}
        />
        <TextField
          label="Bölge"
          size="small"
          fullWidth
          value={filters.region}
          onChange={(e) => setFilters((f) => ({ ...f, region: e.target.value }))}
        />
        <TextField
          label="Alt Bölge"
          size="small"
          fullWidth
          value={filters.subRegion}
          onChange={(e) => setFilters((f) => ({ ...f, subRegion: e.target.value }))}
        />
        <TextField
          select
          label="Rezervasyon İade Tipi"
          size="small"
          fullWidth
          value={filters.refund}
          onChange={(e) => setFilters((f) => ({ ...f, refund: e.target.value as RefundFilter }))}
        >
          <MenuItem value="all">Hepsi</MenuItem>
          <MenuItem value="withPolicy">İptal şartı belirtilmiş</MenuItem>
          <MenuItem value="noPolicy">İptal şartı belirtilmemiş</MenuItem>
        </TextField>

        {effectiveStarOptions.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Yıldız Aralığı
            </Typography>
            {effectiveStarOptions.map((opt) => (
              <FormControlLabel
                key={opt.star}
                sx={{ display: 'flex' }}
                control={
                  <Checkbox
                    size="small"
                    checked={filters.stars.has(opt.star)}
                    onChange={() => setFilters((f) => ({ ...f, stars: toggleSetValue(f.stars, opt.star) }))}
                  />
                }
                label={opt.count > 0 ? `${opt.label} (${opt.count})` : opt.label}
              />
            ))}
          </Box>
        )}

        {priceBounds[1] > priceBounds[0] && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Fiyat Aralığı
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip size="small" label={`TL ${Math.round(filters.priceRange[0])}`} />
              <Chip size="small" label={`TL ${Math.round(filters.priceRange[1])}`} />
            </Stack>
            <Slider
              size="small"
              value={filters.priceRange}
              min={priceBounds[0]}
              max={priceBounds[1]}
              onChange={(_, value) => setFilters((f) => ({ ...f, priceRange: value as [number, number] }))}
              valueLabelDisplay="auto"
            />
          </Box>
        )}

        {boardOptions.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Pansiyon Özellikleri
            </Typography>
            {boardOptions.map(([board, count]) => (
              <FormControlLabel
                key={board}
                sx={{ display: 'flex' }}
                control={
                  <Checkbox
                    size="small"
                    checked={filters.boards.has(board)}
                    onChange={() => setFilters((f) => ({ ...f, boards: toggleSetValue(f.boards, board) }))}
                  />
                }
                label={`${board} (${count})`}
              />
            ))}
          </Box>
        )}

        {effectiveFacilityOptions.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Otel Özellikleri
            </Typography>
            {effectiveFacilityOptions.length > 15 && (
              <TextField
                size="small"
                fullWidth
                placeholder="Özellik ara…"
                value={facilitySearch}
                onChange={(e) => setFacilitySearch(e.target.value)}
                sx={{ mb: 1 }}
              />
            )}
            {visibleFacilities.map(([facility, count]) => (
              <FormControlLabel
                key={facility}
                sx={{ display: 'flex' }}
                control={
                  <Checkbox
                    size="small"
                    checked={filters.facilities.has(facility)}
                    onChange={() =>
                      setFilters((f) => ({ ...f, facilities: toggleSetValue(f.facilities, facility) }))
                    }
                  />
                }
                label={count > 0 ? `${facility} (${count})` : facility}
              />
            ))}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
